import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Headset, Link2, Unlink, RefreshCw, Copy, CheckCircle, XCircle, Loader2, Search, ExternalLink, Database, Play, Pause, Square, ChevronLeft, ChevronRight, ChevronDown, Eye, Clock, FileText } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

export default function ZohoSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState({ connected: false });
  const [copied, setCopied] = useState(false);

  const [config, setConfig] = useState({
    client_id: '',
    client_secret: '',
    org_id: '',
    domain: '.com',
    redirect_uri: `${API_URL}/api/zoho/callback`,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncConfig, setSyncConfig] = useState({
    mode: 'on_demand',
    delay_seconds: 2,
    batch_size: 50,
    batch_pause_seconds: 30,
    auto_refresh_hours: 24,
  });
  const [cachedAccounts, setCachedAccounts] = useState([]);
  const [cachedTotal, setCachedTotal] = useState(0);
  const [cachedPage, setCachedPage] = useState(1);
  const [cachedSearch, setCachedSearch] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountChunks, setAccountChunks] = useState([]);
  const syncPollRef = useRef(null);

  // Prompt templates
  const [promptTemplates, setPromptTemplates] = useState(null);
  const [savingPrompts, setSavingPrompts] = useState(false);
  const [openPrompts, setOpenPrompts] = useState({});

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/zoho/status`, {
        headers: getAuthHeaders(),
      });
      setStatus(res.data);
      // Populate saved config into form
      const saved = res.data.config;
      if (saved) {
        setConfig(prev => ({
          ...prev,
          org_id: saved.org_id || prev.org_id,
          domain: saved.domain || prev.domain,
          redirect_uri: saved.redirect_uri || prev.redirect_uri,
          client_id: saved.has_client_id ? saved.client_id_masked : '',
          client_secret: saved.has_client_secret ? saved.client_secret_masked : '',
        }));
      }
    } catch (e) {
      console.error('Failed to fetch Zoho status:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchSyncStatus();
    fetchCachedAccounts();
    fetchPromptTemplates();
    const handler = (event) => {
      if (event.data?.type === 'zoho_connected') {
        toast.success('Zoho Desk connected successfully!');
        fetchStatus();
      } else if (event.data?.type === 'zoho_error') {
        toast.error(`Zoho connection failed: ${event.data.error}`);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [fetchStatus]);

  const fetchSyncStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/zoho/sync/status`, { headers: getAuthHeaders() });
      setSyncStatus(res.data);
      if (res.data.mode) setSyncConfig(prev => ({ ...prev, mode: res.data.mode, delay_seconds: res.data.delay_seconds || 2, batch_size: res.data.batch_size || 50, batch_pause_seconds: res.data.batch_pause_seconds || 30, auto_refresh_hours: res.data.auto_refresh_hours || 24 }));
      // Auto-poll if syncing
      if (res.data.status === 'syncing' && !syncPollRef.current) {
        syncPollRef.current = setInterval(async () => {
          try {
            const r = await axios.get(`${API_URL}/api/zoho/sync/status`, { headers: getAuthHeaders() });
            setSyncStatus(r.data);
            if (r.data.status !== 'syncing') { clearInterval(syncPollRef.current); syncPollRef.current = null; fetchCachedAccounts(); }
          } catch(e) {}
        }, 5000);
      }
    } catch (e) { console.error('Sync status error:', e); }
  };

  const fetchCachedAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const params = new URLSearchParams({ page: cachedPage, page_size: 10 });
      if (cachedSearch) params.append('search', cachedSearch);
      const res = await axios.get(`${API_URL}/api/zoho/sync/accounts?${params}`, { headers: getAuthHeaders() });
      setCachedAccounts(res.data.accounts || []);
      setCachedTotal(res.data.total || 0);
    } catch (e) { console.error('Cached accounts error:', e); }
    setLoadingAccounts(false);
  };

  useEffect(() => { fetchCachedAccounts(); }, [cachedPage, cachedSearch]);
  useEffect(() => { return () => { if (syncPollRef.current) clearInterval(syncPollRef.current); }; }, []);

  const handleSyncConfigSave = async () => {
    try {
      await axios.post(`${API_URL}/api/zoho/sync/config`, syncConfig, { headers: getAuthHeaders() });
      toast.success('Sync configuration saved');
      fetchSyncStatus();
    } catch (e) { toast.error('Failed to save sync config'); }
  };

  const fetchPromptTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/zoho/prompts`, { headers: getAuthHeaders() });
      setPromptTemplates(res.data);
    } catch (e) { console.error('Failed to fetch prompts:', e); }
  };

  const handleSavePrompts = async () => {
    setSavingPrompts(true);
    try {
      await axios.put(`${API_URL}/api/zoho/prompts`, promptTemplates, { headers: getAuthHeaders() });
      toast.success('Prompt templates saved');
    } catch (e) { toast.error('Failed to save prompts'); }
    setSavingPrompts(false);
  };

  const handleResetPrompts = async () => {
    if (!window.confirm('Reset all prompts to defaults? Your customizations will be lost.')) return;
    try {
      await axios.post(`${API_URL}/api/zoho/prompts/reset`, {}, { headers: getAuthHeaders() });
      toast.success('Prompts reset to defaults');
      fetchPromptTemplates();
    } catch (e) { toast.error('Failed to reset prompts'); }
  };

  const handleSyncStart = async () => {
    try {
      await axios.post(`${API_URL}/api/zoho/sync/start`, {}, { headers: getAuthHeaders() });
      toast.success('Full sync started');
      fetchSyncStatus();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to start sync'); }
  };

  const handleSyncPause = async () => {
    try {
      await axios.post(`${API_URL}/api/zoho/sync/pause`, {}, { headers: getAuthHeaders() });
      toast.success('Sync paused');
      fetchSyncStatus();
    } catch (e) { toast.error('Failed to pause sync'); }
  };

  const handleSyncResume = async () => {
    try {
      await axios.post(`${API_URL}/api/zoho/sync/resume`, {}, { headers: getAuthHeaders() });
      toast.success('Sync resumed');
      fetchSyncStatus();
    } catch (e) { toast.error('Failed to resume sync'); }
  };

  const handleSyncStop = async () => {
    if (!window.confirm('Stop the full sync? Progress is saved and can be resumed later.')) return;
    try {
      await axios.post(`${API_URL}/api/zoho/sync/stop`, {}, { headers: getAuthHeaders() });
      toast.success('Sync stopping...');
      fetchSyncStatus();
    } catch (e) { toast.error('Failed to stop sync'); }
  };

  const handleRefreshAccount = async (accountId) => {
    try {
      await axios.post(`${API_URL}/api/zoho/sync/accounts/${accountId}/refresh`, {}, { headers: getAuthHeaders() });
      toast.success('Account refreshed');
      fetchCachedAccounts();
    } catch (e) { toast.error('Failed to refresh account'); }
  };

  const handleViewAccount = async (accountId) => {
    try {
      const res = await axios.get(`${API_URL}/api/zoho/sync/accounts/${accountId}`, { headers: getAuthHeaders() });
      setSelectedAccount(res.data.account);
      setAccountChunks(res.data.chunks || []);
    } catch (e) { toast.error('Failed to load account details'); }
  };

  const handleSave = async () => {
    // Check if user entered real values (not masked placeholders)
    const clientId = config.client_id.includes('*') ? '' : config.client_id;
    const clientSecret = config.client_secret.includes('*') ? '' : config.client_secret;

    if ((!clientId && !status.config?.has_client_id) || (!clientSecret && !status.config?.has_client_secret) || !config.org_id) {
      toast.error('Client ID, Client Secret, and Organization ID are required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/zoho/config`, {
        client_id: clientId,
        client_secret: clientSecret,
        org_id: config.org_id,
        domain: config.domain,
        redirect_uri: config.redirect_uri,
      }, {
        headers: getAuthHeaders(),
      });
      toast.success('Zoho configuration saved');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await axios.get(`${API_URL}/api/zoho/connect`, {
        headers: getAuthHeaders(),
      });
      window.open(res.data.auth_url, 'zoho_auth', 'width=600,height=700');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start connection');
    }
    setConnecting(false);
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Zoho Desk? AI actors will lose access to client data.')) return;
    try {
      await axios.post(`${API_URL}/api/zoho/disconnect`, {}, {
        headers: getAuthHeaders(),
      });
      toast.success('Zoho disconnected');
      setStatus({ connected: false });
    } catch (e) {
      toast.error('Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await axios.get(`${API_URL}/api/zoho/test`, {
        headers: getAuthHeaders(),
      });
      toast.success(`Connection OK! Found ${res.data.organizations?.length || 0} organization(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection test failed');
    }
    setTesting(false);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await axios.post(`${API_URL}/api/zoho/search-client`,
        { query: searchQuery },
        { headers: getAuthHeaders() },
      );
      setSearchResults(res.data);
    } catch (e) {
      toast.error('Search failed');
    }
    setSearching(false);
  };

  const copyRedirectUri = () => {
    navigator.clipboard.writeText(config.redirect_uri);
    setCopied(true);
    toast.success('Redirect URI copied');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="zoho-settings">
      <div className="flex items-center gap-3">
        <Headset className="h-6 w-6 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Zoho Desk</h1>
          <p className="text-sm text-gray-500">Connect your Zoho Desk to enable AI support agents with live client data</p>
        </div>
      </div>

      {/* Connection Status */}
      <Card data-testid="zoho-connection-status">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            Connection Status
            {status.connected ? (
              <Badge className="bg-green-100 text-green-800">Connected</Badge>
            ) : (
              <Badge variant="outline" className="text-gray-500">Not Connected</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status.connected ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Organization: <strong>{status.org_name}</strong></span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Data Center: <strong>zoho{status.domain}</strong></span>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} data-testid="zoho-test-btn">
                  {testing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                  Test Connection
                </Button>
                <Button variant="destructive" size="sm" onClick={handleDisconnect} data-testid="zoho-disconnect-btn">
                  <Unlink className="h-4 w-4 mr-1" />
                  Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Configure your credentials below, then click "Connect to Zoho Desk".</p>
          )}
        </CardContent>
      </Card>

      {/* Configuration */}
      <Card data-testid="zoho-config-card">
        <CardHeader>
          <CardTitle className="text-base">Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Redirect URI */}
          <div>
            <label className="text-sm font-medium text-gray-700">Redirect URI</label>
            <p className="text-xs text-gray-500 mb-1">Copy this into your Zoho API Console's "Authorized Redirect URI"</p>
            <div className="flex gap-2">
              <Input value={config.redirect_uri} readOnly className="bg-gray-50 font-mono text-xs" data-testid="zoho-redirect-uri" />
              <Button variant="outline" size="sm" onClick={copyRedirectUri} data-testid="zoho-copy-uri">
                {copied ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Data Center */}
          <div>
            <label className="text-sm font-medium text-gray-700">Data Center</label>
            <Select value={config.domain} onValueChange={(v) => setConfig({ ...config, domain: v })}>
              <SelectTrigger data-testid="zoho-domain-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value=".com">zoho.com (US)</SelectItem>
                <SelectItem value=".eu">zoho.eu (Europe)</SelectItem>
                <SelectItem value=".com.au">zoho.com.au (Australia)</SelectItem>
                <SelectItem value=".in">zoho.in (India)</SelectItem>
                <SelectItem value=".com.cn">zoho.com.cn (China)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Client ID */}
          <div>
            <label className="text-sm font-medium text-gray-700">Client ID</label>
            <Input
              placeholder="1000.XXXXXXXXXXXXXXXXXX"
              value={config.client_id}
              onChange={(e) => setConfig({ ...config, client_id: e.target.value })}
              data-testid="zoho-client-id"
            />
          </div>

          {/* Client Secret */}
          <div>
            <label className="text-sm font-medium text-gray-700">Client Secret</label>
            <Input
              type="password"
              placeholder="Enter client secret"
              value={config.client_secret}
              onChange={(e) => setConfig({ ...config, client_secret: e.target.value })}
              data-testid="zoho-client-secret"
            />
          </div>

          {/* Org ID */}
          <div>
            <label className="text-sm font-medium text-gray-700">Organization ID</label>
            <Input
              placeholder="e.g., 2389290"
              value={config.org_id}
              onChange={(e) => setConfig({ ...config, org_id: e.target.value })}
              data-testid="zoho-org-id"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={handleSave} disabled={saving} data-testid="zoho-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save Configuration
            </Button>
            {!status.connected && (
              <Button variant="outline" onClick={handleConnect} disabled={connecting} data-testid="zoho-connect-btn">
                {connecting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Link2 className="h-4 w-4 mr-1" />}
                Connect to Zoho Desk
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Client Search (only when connected) */}
      {status.connected && (
        <Card data-testid="zoho-search-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Search className="h-4 w-4" />
              Test Client Search
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-gray-500">Search for a client by name, email, phone, or any identifier to verify the connection.</p>
            <div className="flex gap-2">
              <Input
                placeholder="Search by name, email, phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="zoho-search-input"
              />
              <Button onClick={handleSearch} disabled={searching} data-testid="zoho-search-btn">
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
            {searchResults && (
              <div className="space-y-2 mt-3" data-testid="zoho-search-results">
                <p className="text-sm font-medium">{searchResults.count} result(s)</p>
                {searchResults.results?.map((account, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg text-sm">
                    <p className="font-medium">{account.accountName}</p>
                    {account.email && <p className="text-gray-500">Email: {account.email}</p>}
                    {account.phone && <p className="text-gray-500">Phone: {account.phone}</p>}
                    <p className="text-gray-400 text-xs mt-1">ID: {account.id}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Data Sync Dashboard (only when connected) */}
      {status.connected && (
        <Card data-testid="zoho-sync-dashboard">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="h-4 w-4 text-indigo-600" />
              Data Sync
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Sync Mode Toggle */}
            <div>
              <label className="text-sm font-medium text-gray-700">Sync Mode</label>
              <div className="grid grid-cols-2 gap-3 mt-1">
                <div
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${syncConfig.mode === 'on_demand' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300'}`}
                  onClick={() => setSyncConfig(prev => ({ ...prev, mode: 'on_demand' }))}
                >
                  <div className="font-medium text-sm">On-Demand</div>
                  <p className="text-xs text-gray-500 mt-0.5">Fetches client data when they identify in chat. Low API usage.</p>
                </div>
                <div
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${syncConfig.mode === 'full_sync' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300'}`}
                  onClick={() => setSyncConfig(prev => ({ ...prev, mode: 'full_sync' }))}
                >
                  <div className="font-medium text-sm">Full Sync</div>
                  <p className="text-xs text-gray-500 mt-0.5">Syncs all accounts in background. Pre-loads everything.</p>
                </div>
              </div>
            </div>

            {/* Full Sync Settings (visible when full_sync selected) */}
            {syncConfig.mode === 'full_sync' && (
              <div className="p-3 bg-indigo-50 rounded-lg space-y-3 border border-indigo-200">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-700">API Call Delay</label>
                    <Select value={String(syncConfig.delay_seconds)} onValueChange={(v) => setSyncConfig(prev => ({ ...prev, delay_seconds: parseInt(v) }))}>
                      <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 second</SelectItem>
                        <SelectItem value="2">2 seconds</SelectItem>
                        <SelectItem value="3">3 seconds</SelectItem>
                        <SelectItem value="5">5 seconds</SelectItem>
                        <SelectItem value="10">10 seconds</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Batch Size</label>
                    <Select value={String(syncConfig.batch_size)} onValueChange={(v) => setSyncConfig(prev => ({ ...prev, batch_size: parseInt(v) }))}>
                      <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10 accounts</SelectItem>
                        <SelectItem value="25">25 accounts</SelectItem>
                        <SelectItem value="50">50 accounts</SelectItem>
                        <SelectItem value="100">100 accounts</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Batch Pause</label>
                    <Select value={String(syncConfig.batch_pause_seconds)} onValueChange={(v) => setSyncConfig(prev => ({ ...prev, batch_pause_seconds: parseInt(v) }))}>
                      <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10 seconds</SelectItem>
                        <SelectItem value="15">15 seconds</SelectItem>
                        <SelectItem value="30">30 seconds</SelectItem>
                        <SelectItem value="60">60 seconds</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Auto-Refresh */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-700">Auto-Refresh Interval</label>
                <Select value={String(syncConfig.auto_refresh_hours)} onValueChange={(v) => setSyncConfig(prev => ({ ...prev, auto_refresh_hours: parseInt(v) }))}>
                  <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="12">Every 12 hours</SelectItem>
                    <SelectItem value="24">Every 24 hours</SelectItem>
                    <SelectItem value="48">Every 48 hours</SelectItem>
                    <SelectItem value="72">Every 72 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button size="sm" className="h-8 text-xs" onClick={handleSyncConfigSave}>
                  Save Sync Config
                </Button>
              </div>
            </div>

            {/* Sync Progress (visible when syncing/paused) */}
            {syncStatus && (syncStatus.status === 'syncing' || syncStatus.status === 'paused' || syncStatus.status === 'completed') && (
              <div className="p-3 bg-gray-50 rounded-lg border space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">
                    {syncStatus.status === 'syncing' && <span className="text-blue-600">Syncing...</span>}
                    {syncStatus.status === 'paused' && <span className="text-yellow-600">Paused</span>}
                    {syncStatus.status === 'completed' && <span className="text-green-600">Completed</span>}
                  </span>
                  <span className="text-xs text-gray-500">
                    {syncStatus.progress?.synced || 0} / {syncStatus.progress?.total || '?'} accounts
                  </span>
                </div>
                {/* Progress bar */}
                {syncStatus.progress?.total > 0 && (
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${syncStatus.status === 'paused' ? 'bg-yellow-500' : syncStatus.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'}`}
                      style={{ width: `${Math.min(100, (syncStatus.progress.synced / syncStatus.progress.total) * 100)}%` }}
                    />
                  </div>
                )}
                {syncStatus.progress?.message && (
                  <p className="text-xs text-gray-500">{syncStatus.progress.message}</p>
                )}
              </div>
            )}

            {/* Sync Controls */}
            <div className="flex gap-2">
              {syncConfig.mode === 'full_sync' && (
                <>
                  {(!syncStatus || syncStatus.status === 'idle' || syncStatus.status === 'completed' || syncStatus.status === 'stopped' || syncStatus.status === 'error') && (
                    <Button size="sm" className="h-8 text-xs bg-green-600 hover:bg-green-700" onClick={handleSyncStart}>
                      <Play className="h-3 w-3 mr-1" /> Start Full Sync
                    </Button>
                  )}
                  {syncStatus?.status === 'syncing' && (
                    <>
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleSyncPause}>
                        <Pause className="h-3 w-3 mr-1" /> Pause
                      </Button>
                      <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={handleSyncStop}>
                        <Square className="h-3 w-3 mr-1" /> Stop
                      </Button>
                    </>
                  )}
                  {syncStatus?.status === 'paused' && (
                    <>
                      <Button size="sm" className="h-8 text-xs bg-green-600 hover:bg-green-700" onClick={handleSyncResume}>
                        <Play className="h-3 w-3 mr-1" /> Resume
                      </Button>
                      <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={handleSyncStop}>
                        <Square className="h-3 w-3 mr-1" /> Stop
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Sync Statistics */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-2 bg-white rounded border">
                <div className="text-lg font-bold text-indigo-600">{syncStatus?.accounts_cached || 0}</div>
                <div className="text-xs text-gray-500">Accounts Cached</div>
              </div>
              <div className="p-2 bg-white rounded border">
                <div className="text-lg font-bold text-cyan-600">{syncStatus?.chunks_total || 0}</div>
                <div className="text-xs text-gray-500">Total Chunks</div>
              </div>
              <div className="p-2 bg-white rounded border">
                <div className="text-lg font-bold text-green-600">{syncStatus?.chunks_embedded || 0}</div>
                <div className="text-xs text-gray-500">Chunks Embedded</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cached Accounts (only when connected) */}
      {status.connected && (
        <Card data-testid="zoho-cached-accounts">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="h-4 w-4" />
              Cached Accounts ({cachedTotal})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Search */}
            <div className="flex gap-2">
              <Input
                placeholder="Search cached accounts..."
                value={cachedSearch}
                onChange={(e) => { setCachedSearch(e.target.value); setCachedPage(1); }}
                className="text-xs h-8"
              />
            </div>

            {/* Account List */}
            {loadingAccounts ? (
              <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
            ) : cachedAccounts.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No cached accounts yet. Accounts are cached when clients identify themselves or via full sync.</p>
            ) : (
              <div className="space-y-2">
                {cachedAccounts.map((acc) => (
                  <div key={acc.account_id} className="p-2.5 bg-gray-50 rounded-lg border text-xs flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{acc.account_name}</div>
                      <div className="text-gray-500 truncate">{acc.email || acc.phone || '—'}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-gray-400">{acc.chunk_count} chunks</span>
                        <span className="text-gray-400">{acc.ticket_count} tickets</span>
                        <span className="text-gray-400">{acc.contact_count} contacts</span>
                        {acc.embedded ? (
                          <Badge className="bg-green-100 text-green-700 text-xs px-1 py-0">Embedded</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs px-1 py-0 text-yellow-600">Not Embedded</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleViewAccount(acc.account_id)} title="View chunks">
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleRefreshAccount(acc.account_id)} title="Re-sync">
                        <RefreshCw className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {cachedTotal > 10 && (
              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-gray-400">Page {cachedPage} of {Math.ceil(cachedTotal / 10)}</span>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={() => setCachedPage(p => Math.max(1, p - 1))} disabled={cachedPage === 1}>
                    <ChevronLeft className="h-3 w-3" />
                  </Button>
                  <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={() => setCachedPage(p => p + 1)} disabled={cachedPage >= Math.ceil(cachedTotal / 10)}>
                    <ChevronRight className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Account Detail Modal */}
      {selectedAccount && (
        <Card className="border-2 border-indigo-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{selectedAccount.account_name}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setSelectedAccount(null); setAccountChunks([]); }}>Close</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div><span className="text-gray-400">Email:</span> {selectedAccount.email || '—'}</div>
              <div><span className="text-gray-400">Phone:</span> {selectedAccount.phone || '—'}</div>
              <div><span className="text-gray-400">Synced:</span> {selectedAccount.synced_at ? new Date(selectedAccount.synced_at).toLocaleString() : '—'}</div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-700">{accountChunks.length} Chunks:</p>
              {accountChunks.map((chunk, idx) => (
                <div key={idx} className="p-2 bg-gray-50 rounded border text-xs">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs px-1 py-0">{chunk.chunk_type}</Badge>
                    {chunk.embedded ? (
                      <Badge className="bg-green-100 text-green-700 text-xs px-1 py-0">Embedded</Badge>
                    ) : (
                      <Badge variant="outline" className="text-yellow-600 text-xs px-1 py-0">Not Embedded</Badge>
                    )}
                  </div>
                  <pre className="text-xs text-gray-600 whitespace-pre-wrap max-h-32 overflow-y-auto">{chunk.text}</pre>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Prompt Templates (only when connected) */}
      {status.connected && promptTemplates && (
        <Card data-testid="zoho-prompt-templates">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4 text-indigo-600" />
                  Prompt Templates
                </CardTitle>
                <p className="text-xs text-gray-500 mt-1">Configure the LLM prompts used during client identification and support conversations. Use variables like {'{message}'}, {'{language}'} in your prompts.</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-xs h-7" onClick={handleResetPrompts}>Reset Defaults</Button>
                <Button size="sm" className="text-xs h-7" onClick={handleSavePrompts} disabled={savingPrompts}>
                  {savingPrompts ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}Save Prompts
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Prompt accordions */}
            {Object.entries(promptTemplates.prompts || {}).map(([key, tmpl]) => (
              <div key={key} className="border rounded-lg overflow-hidden">
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-2.5 bg-gray-50 hover:bg-gray-100 text-left"
                  onClick={() => setOpenPrompts(prev => ({ ...prev, [key]: !prev[key] }))}
                >
                  <div>
                    <span className="text-sm font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                    <span className="text-xs text-gray-400 ml-2">{tmpl.description}</span>
                  </div>
                  <ChevronDown className={`h-4 w-4 transition-transform ${openPrompts[key] ? 'rotate-180' : ''}`} />
                </button>
                {openPrompts[key] && (
                  <div className="p-3 space-y-2">
                    <textarea
                      value={tmpl.prompt || ''}
                      onChange={(e) => setPromptTemplates(prev => ({
                        ...prev,
                        prompts: { ...prev.prompts, [key]: { ...prev.prompts[key], prompt: e.target.value } }
                      }))}
                      rows={4}
                      className="w-full px-3 py-2 text-xs font-mono border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-gray-400">Variables: {(tmpl.variables || []).join(', ')}</p>
                  </div>
                )}
              </div>
            ))}

            {/* Identifier Format Hints */}
            <div className="pt-3 border-t">
              <p className="text-sm font-medium mb-2">Identifier Format Hints</p>
              <p className="text-xs text-gray-400 mb-2">Help the LLM distinguish between similar identifier formats (e.g., phone vs CUIT)</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(promptTemplates.identifier_hints || {}).map(([key, value]) => (
                  <div key={key}>
                    <label className="text-xs font-medium text-gray-600 capitalize">{key}</label>
                    <Input
                      value={value || ''}
                      onChange={(e) => setPromptTemplates(prev => ({
                        ...prev,
                        identifier_hints: { ...prev.identifier_hints, [key]: e.target.value }
                      }))}
                      placeholder={`e.g., ${key === 'phone' ? '10 digits' : key === 'cuit' ? '11 digits, XX-XXXXXXXX-X' : key === 'id' ? '4-6 digits' : '6 digits'}`}
                      className="text-xs h-8"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Verification Method */}
            <div className="pt-3 border-t">
              <p className="text-sm font-medium mb-2">Verification Method</p>
              <p className="text-xs text-gray-400 mb-2">How to verify identity after finding a matching account</p>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="radio"
                    checked={promptTemplates.verification_method === 'ask_detail'}
                    onChange={() => setPromptTemplates(prev => ({ ...prev, verification_method: 'ask_detail' }))}
                  />
                  Ask for additional detail (email, phone, CUIT)
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="radio"
                    checked={promptTemplates.verification_method === 'accept_name'}
                    onChange={() => setPromptTemplates(prev => ({ ...prev, verification_method: 'accept_name' }))}
                  />
                  Accept name as verification
                </label>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Help */}
      <Card>
        <CardContent className="pt-6">
          <div className="text-sm text-gray-600 space-y-2">
            <p className="font-medium">How to get your Zoho credentials:</p>
            <ol className="list-decimal list-inside space-y-1 text-xs">
              <li>Go to <a href="https://api-console.zoho.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">api-console.zoho.com</a> <ExternalLink className="inline h-3 w-3" /></li>
              <li>Create a "Server-based Application"</li>
              <li>Copy the Redirect URI from above into the "Authorized Redirect URIs" field</li>
              <li>Copy your Client ID and Client Secret into the fields above</li>
              <li>For Org ID: go to Zoho Desk Setup - Developer Space - API</li>
              <li>Click "Save Configuration" then "Connect to Zoho Desk"</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
